import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import {
  AD_PROVIDERS,
  AD_PROVIDER_LABELS,
  CRYPTO_NETWORKS,
  DEFAULT_NETWORK,
  DEFAULT_TOKEN,
  TRANSACTION_TYPE_LABELS,
  accountLabel,
  explorerUrl,
  usesCabinetBalance,
  fromMicros,
  percentOf,
  toMicros,
  tokensForNetwork,
  type AdProvider,
} from "@app/shared";
import { api, type CabinetBalance, type NewTopUp } from "@/lib/api";
import { AllocateDialog, ReturnDialog } from "@/components/data/CabinetMoneyDialogs";
import { money, pct } from "@/lib/format";
import { useProviderFilter } from "@/lib/provider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { ChipGroup } from "@/components/app/ChipGroup";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SelectField, TextField, TextareaField, useNotify } from "@/components/app/page";

const DECIMAL = /^-?\d+(\.\d+)?$/;
/** Micros from what is being typed, or null while it is not a number yet. */
/** 100% in micros: the base a refund's reverse rate divides by. */
const HUNDRED = toMicros("100");

const parse = (value: string): bigint | null => (DECIMAL.test(value.trim()) ? toMicros(value.trim()) : null);
const usd = (micros: bigint) => money(fromMicros(micros, 2));
const signed = (micros: bigint) => `${micros > 0n ? "+" : ""}${usd(micros)}`;

/** A label over a control that is a group rather than one input. */
function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="text-sm leading-none font-medium">{label}</span>
      {children}
    </div>
  );
}

/**
 * One panel for every kind of money movement, from both entry points: the
 * client page fixes the client, the header lets one be picked.
 *
 * The preview is computed with the same bigint helpers the server uses, so the
 * figures shown are the figures that will be booked, not a float estimate.
 */
export function TransactionSheet({
  open,
  onOpenChange,
  clientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId?: number;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const { scope } = useProviderFilter();
  const clients = useQuery({ queryKey: ["clients"], queryFn: api.clients, enabled: open });

  const [picked, setPicked] = useState<number | undefined>(clientId);
  const [type, setType] = useState<"deposit" | "refund">("deposit");
  const [provider, setProvider] = useState<AdProvider>(scope ?? "moloco");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState<"crypto" | "fiat">("crypto");
  const [network, setNetwork] = useState(DEFAULT_NETWORK);
  const [token, setToken] = useState(DEFAULT_TOKEN);
  const [txHash, setTxHash] = useState("");
  const [withCommission, setWithCommission] = useState(true);
  const [refundRate, setRefundRate] = useState("");

  useEffect(() => {
    if (!open) return;
    setPicked(clientId);
    setType("deposit");
    // Whatever platform the dashboard is on is the likeliest target.
    setProvider(scope ?? "moloco");
    // Scope is read once per opening on purpose: switching platforms with the
    // panel open should not silently retarget a half-filled transaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  const target = clientId ?? picked;
  const options = clients.data ?? [];
  const selectedClient = options.find((c) => c.id === target);

  // The ladder rate for this client right now: what a deposit is charged, and
  // the starting point for a refund's rate, which the admin may then change.
  const ladderRate = useMemo(() => clients.data?.find((c) => c.id === target)?.ratePercent, [clients.data, target]);

  useEffect(() => {
    if (ladderRate !== undefined) setRefundRate(ladderRate);
  }, [ladderRate, open]);

  // Cabinets of the client that keep money on themselves. Picking one here is
  // not a transaction from outside but a move of the client's own money, so it
  // hands over to the funding window rather than pretending to be a deposit.
  const cabinets = useQuery({
    queryKey: ["cabinets", target],
    queryFn: () => api.cabinets(target!),
    enabled: open && target !== undefined,
  });
  const [handoff, setHandoff] = useState<CabinetBalance | null>(null);

  const balances = useQuery({
    queryKey: ["balances", target],
    queryFn: () => api.balances(target!),
    enabled: open && target !== undefined,
  });
  const balanceBefore = toMicros(balances.data?.find((b) => b.provider === provider)?.availableBalance ?? "0");

  const changeNetwork = (id: string) => {
    setNetwork(id);
    const available = tokensForNetwork(id);
    if (!available.includes(token)) setToken(available[0] ?? DEFAULT_TOKEN);
  };

  const amountMicros = parse(amount);
  const validAmount = amountMicros !== null && amountMicros > 0n;
  const rateMicros = parse(refundRate);
  const rateValid =
    !withCommission || (rateMicros !== null && rateMicros >= -100_000_000n && rateMicros <= 100_000_000n);
  const effectiveRefundRate = withCommission && rateMicros !== null ? refundRate.trim() : "0";

  /** Every figure the preview shows, as signed micros. */
  const preview = useMemo(() => {
    if (!validAmount) return null;
    if (type === "deposit") {
      // Money onto a platform's account is not charged here; the commission is
      // taken when it is moved onto one of that platform's cabinets.
      if (usesCabinetBalance(provider)) {
        return { kind: "deposit" as const, fee: 0n, net: amountMicros, rate: "0" };
      }
      if (!ladderRate) return null;
      const fee = percentOf(amountMicros, ladderRate);
      return { kind: "deposit" as const, fee, net: amountMicros - fee, rate: ladderRate };
    }
    if (!rateValid) return null;
    // The reverse of a deposit, so paying money back leaves the client where
    // they started: 900 off the balance at 16% pays out 900 / 0.84.
    const payout = toMicros(fromMicros((amountMicros * HUNDRED) / (HUNDRED - toMicros(effectiveRefundRate)), 2));
    return {
      kind: "refund" as const,
      fee: payout - amountMicros,
      payout,
      net: -amountMicros,
      rate: effectiveRefundRate,
    };
  }, [validAmount, amountMicros, type, provider, ladderRate, rateValid, effectiveRefundRate]);

  const balanceAfter = preview ? balanceBefore + preview.net : null;

  const submit = useMutation({
    mutationFn: () => {
      const body: NewTopUp = {
        type,
        provider,
        grossAmount: amount.trim(),
        paymentMethod: method,
        note: note.trim() || undefined,
      };
      if (type === "refund") body.refundRatePercent = effectiveRefundRate;
      if (method === "crypto") {
        body.network = network;
        body.token = token;
        body.txHash = txHash.trim() || undefined;
      }
      return api.addTopUp(target!, body);
    },
    onSuccess: (t) => {
      for (const key of ["clients", "stats", "client", "topups", "allTopUps", "balances"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
      const platform = accountLabel(t.provider);
      notify(
        t.type === "refund" ? `Возврат проведён · ${platform}` : `Пополнение проведено · ${platform}`,
        t.type === "refund"
          ? `С баланса ${money(t.netAmount)} · клиенту ${money(String(-Number(t.grossAmount)))}`
          : `${money(t.grossAmount)} · комиссия ${pct(t.ratePercent)} → на баланс ${money(t.netAmount)}`,
        "green",
      );
      setAmount("");
      setNote("");
      setTxHash("");
      onOpenChange(false);
    },
    onError: (e: Error) =>
      notify(
        e.message === "DUPLICATE_TX_HASH" ? "Транзакция с таким хешем уже проведена" : "Не удалось провести транзакцию",
        undefined,
        "destructive",
      ),
  });

  const link = explorerUrl(network, txHash);
  const platformLabel = accountLabel(provider);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent width="min(32rem, 100vw - 1rem)" closeLabel="Закрыть">
          <SheetHeader>
            <SheetTitle>Добавить транзакцию</SheetTitle>
            <SheetDescription>Движение денег по балансу клиента на одной платформе.</SheetDescription>
          </SheetHeader>

          <SheetBody className="flex flex-col gap-5">
            {clientId === undefined && (
              <Group label="Клиент">
                {/* Searchable rather than a plain list: there are enough clients
                  that scrolling through them is the slow way in. */}
                <Combobox
                  options={options.map((c) => ({ value: String(c.id), label: c.title }))}
                  value={picked === undefined ? "" : String(picked)}
                  onValueChange={(v) => setPicked(Number(v))}
                  placeholder="Выберите клиента"
                  searchPlaceholder="Найти клиента…"
                  emptyMessage="Ничего не найдено"
                />
                {selectedClient && (
                  <span className="text-muted-foreground text-xs tabular-nums">
                    Ставка {pct(selectedClient.ratePercent)} · остаток {money(selectedClient.availableBalance)}
                  </span>
                )}
              </Group>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              {/* A select rather than a switch: more transaction types are coming,
                and a row of segments stops fitting at the third. */}
              <SelectField
                label="Тип"
                options={(["deposit", "refund"] as const).map((t) => ({ value: t, label: TRANSACTION_TYPE_LABELS[t] }))}
                value={type}
                onValueChange={(v) => setType(v as "deposit" | "refund")}
              />
              {/* Money from outside lands on a platform's account -- or, for
                  Moloco, on its master balance. A cabinet cannot be paid into
                  from outside, only funded from its platform's account, so
                  picking one here hands over to that window. */}
              <SelectField
                label={type === "refund" ? "Откуда" : "Куда"}
                description={
                  usesCabinetBalance(provider)
                    ? "На счёт клиента в этой платформе, без комиссии. Комиссия удержится при пополнении кабинета."
                    : "Сразу на мастер-баланс Moloco, комиссия по лесенке."
                }
                options={[
                  ...AD_PROVIDERS.map((p) => ({ value: p, label: accountLabel(p) })),
                  ...(cabinets.data ?? []).map((c) => ({
                    value: `cab:${c.adAccountId}`,
                    label: `${AD_PROVIDER_LABELS[c.provider]} · ${c.title}`,
                  })),
                ]}
                value={provider}
                onValueChange={(v) => {
                  const cabinet = v.startsWith("cab:")
                    ? (cabinets.data ?? []).find((c) => c.adAccountId === Number(v.slice(4)))
                    : undefined;
                  // A cabinet is funded from the client's account, with its own
                  // preview of both balances -- that window, not this form.
                  if (cabinet) {
                    setHandoff(cabinet);
                    onOpenChange(false);
                    return;
                  }
                  setProvider(v as AdProvider);
                }}
              />
            </div>

            <Group label="Способ оплаты">
              <ChipGroup
                label="Способ оплаты"
                fullWidth
                options={[
                  { value: "crypto", label: "Криптовалюта" },
                  { value: "fiat", label: "Фиат" },
                ]}
                value={method}
                onValueChange={setMethod}
              />
            </Group>

            {method === "crypto" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <SelectField
                    label="Сеть"
                    options={CRYPTO_NETWORKS.map((n) => ({ value: n.id, label: n.label }))}
                    value={network}
                    onValueChange={changeNetwork}
                  />
                  <SelectField
                    label="Токен"
                    options={tokensForNetwork(network).map((t) => ({ value: t, label: t }))}
                    value={token}
                    onValueChange={setToken}
                  />
                </div>
                <TextField
                  label="Хеш транзакции"
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  placeholder="хеш или ссылка на обозреватель"
                  className="font-mono text-xs"
                  spellCheck={false}
                  description={
                    txHash.trim() && link ? (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary inline-flex items-center gap-1 hover:underline"
                      >
                        Проверить в обозревателе
                        <ExternalLink className="size-3" />
                      </a>
                    ) : undefined
                  }
                />
              </>
            )}

            <TextField
              // Balances are kept in dollars whatever the token, so the unit is $.
              label={type === "refund" ? "Сумма к списанию с баланса, $" : "Сумма пополнения, $"}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={type === "refund" ? "900" : "10000"}
              className="tabular-nums"
            />

            {type === "refund" && (
              <Group label="Комиссия">
                <ChipGroup
                  label="Комиссия"
                  fullWidth
                  options={[
                    { value: "on", label: "С комиссией" },
                    { value: "off", label: "Без комиссии" },
                  ]}
                  value={withCommission ? "on" : "off"}
                  onValueChange={(v) => setWithCommission(v === "on")}
                />
                {withCommission && (
                  <TextField
                    label="Ставка возврата"
                    inputMode="decimal"
                    value={refundRate}
                    onChange={(e) => setRefundRate(e.target.value)}
                    className="tabular-nums"
                    icon={<span className="text-muted-foreground text-sm">%</span>}
                    iconPosition="end"
                    error={rateValid ? undefined : "Ставка от −100 до 100"}
                    description={`Сейчас по лесенке ${ladderRate ? pct(ladderRate) : "—"}. Плюс — комиссия возвращается клиенту сверху, минус — удерживается из возврата.`}
                  />
                )}
              </Group>
            )}

            <TextareaField
              label="Описание"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="необязательно"
            />

            {preview && (
              <div className="bg-secondary/50 border-border grid gap-1 rounded-xl border p-3 text-sm">
                {preview.kind === "deposit" ? (
                  <>
                    <Row label="Сумма пополнения" value={usd(amountMicros!)} />
                    {preview.fee === 0n ? (
                      <Row label="Комиссия" value="не удерживается" muted />
                    ) : (
                      <Row label={`Комиссия ${pct(preview.rate)}`} value={`−${usd(preview.fee)}`} muted />
                    )}
                    <Row
                      label={usesCabinetBalance(provider) ? "Зачислится на счёт" : "Зачислится на баланс"}
                      value={`+${usd(preview.net)}`}
                      strong
                      accent="positive"
                    />
                  </>
                ) : (
                  <>
                    <Row label="Спишется с баланса" value={usd(preview.net)} />
                    {preview.fee !== 0n && (
                      <Row
                        label={
                          preview.fee > 0n
                            ? `Возврат комиссии ${pct(preview.rate)}`
                            : `Удержание ${pct(fromMicros(-toMicros(preview.rate), 2))}`
                        }
                        value={signed(preview.fee)}
                        muted
                      />
                    )}
                    <Row label="Клиент получит" value={usd(preview.payout)} strong />
                  </>
                )}
                {target !== undefined && balanceAfter !== null && (
                  <div className="border-border mt-1 border-t pt-2">
                    <Row
                      label={`Баланс ${platformLabel}`}
                      value={`${usd(balanceBefore)} → ${usd(balanceAfter)}`}
                      accent={balanceAfter < 0n ? "negative" : undefined}
                    />
                    {preview.kind === "refund" && balanceAfter < 0n && (
                      <p className="text-destructive mt-1 text-xs">
                        Возврат больше остатка на {platformLabel}: баланс уйдёт в минус.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </SheetBody>

          <SheetFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button onClick={() => submit.mutate()} disabled={!target || !preview || submit.isPending}>
              {submit.isPending ? "Проводим…" : type === "refund" ? "Провести возврат" : "Провести пополнение"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {target !== undefined && (
        <>
          <AllocateDialog
            clientId={target}
            cabinet={type === "refund" ? null : handoff}
            onOpenChange={(o) => !o && setHandoff(null)}
          />
          <ReturnDialog cabinet={type === "refund" ? handoff : null} onOpenChange={(o) => !o && setHandoff(null)} />
        </>
      )}
    </>
  );
}

function Row({
  label,
  value,
  muted,
  strong,
  accent,
}: {
  label: string;
  value: string;
  muted?: boolean;
  strong?: boolean;
  accent?: "positive" | "negative";
}) {
  return (
    <div className={cn("flex justify-between gap-3", muted && "text-muted-foreground", strong && "font-medium")}>
      <span>{label}</span>
      <span
        className={cn(
          "text-right tabular-nums",
          accent === "positive" && "text-green-soft-foreground",
          accent === "negative" && "text-destructive",
        )}
      >
        {value}
      </span>
    </div>
  );
}
